/**
 * Meraki keys store — load + create + activate, with celery task tracking.
 *
 * Fall-back behavior matches the workflows store: API failure swaps in the
 * fixture data and surfaces a non-blocking warning rather than blanking out
 * the page.
 */
import { create } from "zustand";
import {
  get as apiGet,
  post as apiPost,
  patch as apiPatch,
  del as apiDel,
  ApiError,
} from "@/api/client";
import { pollCeleryTask } from "@/api/celery";
import {
  MerakiKeyListSchema,
  CeleryTaskSchema,
  type MerakiKey,
  type MerakiKeyMutation,
  type CeleryProgress,
} from "@/api/schemas/meraki";
import { MOCK_MERAKI_KEYS } from "@/api/fixtures/meraki-keys";
import { useAuth } from "@/stores/auth";

interface State {
  /** All registered keys for the current user. */
  data: MerakiKey[];
  /** Lifecycle status — see workflows store for the same shape. */
  status: "idle" | "loading" | "ready" | "error";
  /** Non-blocking error message; `data` may still be populated from fixtures. */
  error: string | null;

  /** Fetch the current user's registered keys. */
  load: () => Promise<void>;
  /** Create a new key. Returns the celery task id used to poll sync progress. */
  create: (payload: MerakiKeyMutation) => Promise<string | null>;
  /** Mark an existing key active (only one key can be active at a time). */
  activate: (id: number) => Promise<void>;
  /** Remove a key. If it was the active one, the user lands keyless
   *  until they register another (the auth `merakiKeyConfigured` flag
   *  flips back to false; the ProtectedRoute key gate kicks in). */
  remove: (id: number) => Promise<void>;
  /** Rename only the key label. Secret key material is immutable. */
  rename: (id: number, name: string) => Promise<void>;
  /** Poll a celery task and yield progress until it completes. */
  pollTask: (taskId: string, onProgress: (p: CeleryProgress) => void) => Promise<CeleryProgress>;
}

export const useMerakiKeys = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/meraki/keys/");
      const parsed = MerakiKeyListSchema.parse(raw);
      set({ data: parsed, status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_MERAKI_KEYS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },

  create: async (payload) => {
    // POST /meraki/keys/ → { taskId }. Whether the celery task is returned
    // is best-effort — the key may be created without sync if the queue is down.
    const raw = await apiPost<unknown>("/meraki/keys/", payload);
    const parsed = CeleryTaskSchema.safeParse(raw);
    // Refresh local state regardless of whether the task id came back.
    void get().load();
    // Also refresh auth so the `merakiKeyConfigured` flag flips to true —
    // otherwise the ProtectedRoute key gate would keep bouncing the user
    // back to /meraki/keys after they registered one.
    void useAuth.getState().bootstrap();
    return parsed.success ? parsed.data.taskId : null;
  },

  activate: async (id) => {
    await apiPatch(`/meraki/keys/${id}/`, { active: true });
    // Mutate optimistically so the UI flips immediately; reload to confirm.
    set((s) => ({
      data: s.data.map((k) => ({ ...k, active: k.id === id })),
    }));
    void get().load();
    // Activating an inactive key may flip `merakiKeyConfigured` from false
    // to true (if the user had keys but none active); refresh auth too.
    void useAuth.getState().bootstrap();
  },

  remove: async (id) => {
    await apiDel(`/meraki/keys/${id}/`);
    // Drop the row locally + refresh from server.
    set((s) => ({ data: s.data.filter((k) => k.id !== id) }));
    void get().load();
    // `merakiKeyConfigured` may flip to false if we just removed the
    // active key — keeps the ProtectedRoute key gate honest.
    void useAuth.getState().bootstrap();
  },

  rename: async (id, name) => {
    await apiPatch(`/meraki/keys/${id}/`, { name });
    set((s) => ({
      data: s.data.map((k) => (k.id === id ? { ...k, name } : k)),
    }));
    void get().load();
  },

  pollTask: pollCeleryTask,
}));
